import _get from 'lodash/get';
import jwt from 'jsonwebtoken';
import * as UimWebSdk from '@uim/web-sdk';
import { logException, appendQueryString, ROUTES } from 'Utils';
const UIM_SDK_ENV = process.env.REACT_APP_UIM_SDK_ENV;
const UIM_APP_ID = process.env.REACT_APP_UIM_APP_ID;
const UIM_USER_ID_FIELD = process.env.REACT_APP_UIM_USER_ID_FIELD;
const loginUIM = {};

// check if OAuth implementation, switch to param application set
const getOAuthApplicationId = () => {
  if (typeof window !== 'undefined') {
    const params = new URLSearchParams(window.location.search);
    return params.get('uimApplicationId');
  }

  return null;
};

/**
 * UIM Login
 *
 * @param username
 * @param password
 */
loginUIM.login = async (username, password) => {
  const user = await loginUIM.uimApi.login(username, password);
  return user;
};

/**
 * UIM Logout
 */
loginUIM.logout = async () => {
  try {
    await loginUIM.uimApi.logout();
  } catch (e) {
    logException(e, 'Failed to logout');
  }
};

/**
 * UIM Delete Account
 */
loginUIM.deleteAccount = async () => {
  try {
    await loginUIM.uimApi.authenticatedUser.deleteAccount();
    if (window) {
      window.location.href = appendQueryString(
        ROUTES.PAGE_HOME.url,
        window.location.search
      );
    }
  } catch (e) {
    logException(e, 'Delete account failed');
  }
};

/**
 * Resend registration verification email
 */
loginUIM.resendRegistrationVerificationEmail = async (email) => {
  try {
    await loginUIM.uimApi.resendRegistrationVerificationEmail(email);
    return true;
  } catch (e) {
    logException(e, 'resendRegistrationVerificationEmail failed');
    return false;
  }
};

/**
 * Resend registration verification email to authenticated users
 */
loginUIM.resendVerificationEmail = async () => {
  if (loginUIM.uimApi.authenticatedUser) {
    try {
      await loginUIM.uimApi.authenticatedUser.resendVerificationEmail();
      return true;
    } catch (e) {
      logException(e, 'resendVerificationEmail failed');
      return false;
    }
  }
  return Promise.reject('User has no session');
};

loginUIM.getAuthenticatedUser = () => loginUIM.uimApi.authenticatedUser;

/**
 * Update password
 * @param resetPasswordToken
 * @param newPassword
 */
loginUIM.setNewPassword = async (resetPasswordToken, newPassword) => {
  const user = await loginUIM.uimApi.setNewPassword(
    resetPasswordToken,
    newPassword
  );
  return user;
};

/**
 * Register with UIM
 *
 * @param user
 * @returns {Promise.<boolean>}
 */
loginUIM.register = (user) => {
  const profileFields = {
    email: user.email,
    first_name: user.firstName,
    last_name: user.lastName,
  };
  const registration = loginUIM.uimApi
    .newRegistration()
    .setLanguage(user.language)
    .setCountry(user.country)
    .setPassword(user.pw)
    .addProfileFieldSet(profileFields)
    .addPolicySet(user.policies)
    .addNewsletterList(user.newsletters)
    // .addNewsletterList() //TODO
    .build();
  return loginUIM.uimApi.register(registration); // returns Promise<IAuthenticatedUser>
};

/**
 * True if the user registered through the build-in UIM form.
 * A "native" account is the standard one that uses the UIM, in contrast to a social-media account.
 *
 * @returns true or false
 */
loginUIM.hasEmailLogin = async () => {
  try {
    const isNative = await loginUIM.getAuthenticatedUser().isNative();
    return isNative;
  } catch (e) {
    logException(e);
    return false;
  }
};

/**
 * True if the user is an existing account
 * @returns true or false
 */
loginUIM.isExistingAccount = async () => {
  try {
    const isExisting = await loginUIM
      .getAuthenticatedUser()
      .isInitialPoliciesAccepted();
    return isExisting;
  } catch (e) {
    logException(e);
    return false;
  }
};

/**
 * Takes in UIM's registration and transforms it into three arrays
 * (implicit policies, explicit policies, and newsletters)
 */
loginUIM.transformRegistrationItems = (items, country) => {
  const policies = (items.policiesRendering || []).map((p) => ({
    id: p.ids && p.ids.length && p.ids.join(','),
    checkbox: p.checkbox,
    // if there's a checkbox default US to checked, otherwise default to true since it's implicit
    checked: p.checkbox ? country === 'US' : true,
    text: p.text,
  }));
  const newsletters = (items.newsletters || []).map((n) => ({
    ...n,
    checked: country === 'US',
  }));

  const implicitPolicies = policies.filter((p) => !p.checkbox);
  const explicitPolicies = policies.filter((p) => p.checkbox);

  return {
    implicitPolicies,
    explicitPolicies,
    newsletters,
  };
};

/**
 * Internal: fetch registration-items
 */
loginUIM.fetchRegistrationItems = async (country) => {
  if (!loginUIM.registrationItems && loginUIM.uimApi) {
    // TODO: flip the flag to true to get country list when we need to add country combo-box
    const items = await loginUIM.uimApi.getRegistrationItems(false);
    loginUIM.registrationItems = loginUIM.transformRegistrationItems(
      items,
      country
    );
  }
  return loginUIM.registrationItems;
};

/**
 * Check if the email is used in UIM
 *
 * @param email
 * @returns {Promise.<*>}
 */
loginUIM.isEmailAvailable = (email) =>
  loginUIM.uimApi.checkUniqueUsername('email', email);

loginUIM.getProfileValues = () => {
  if (
    loginUIM.uimApi.authenticatedUser &&
    loginUIM.uimApi.authenticatedUser.getProfileValues
  ) {
    const values = loginUIM.uimApi.authenticatedUser.getProfileValues();
    return values && values.value;
  }
  return {};
};

loginUIM.getJWT = async () => {
  try {
    if (loginUIM.uimApi.authenticatedUser) {
      return await loginUIM.uimApi.authenticatedUser.getJWT();
    }
  } catch (e) {
    logException(e);
  }
  return null;
};

loginUIM.parseJWT = (token) => {
  return jwt.decode(token);
};

/**
 * initialize function called from the app to add a hook
 * in order to update the app if UIM detects a user in the browser session
 *
 * @param configData
 * @returns {Promise.<void>}
 */
const initialize = (configData) => {
  CONFIG.country = configData.country || CONFIG.country;
  CONFIG.language = configData.language || CONFIG.language;
  CONFIG.application = configData.application || CONFIG.application;
};

//
// =========================================================
//

const fetchAuthenticatedUser = async () => {
  try {
    return await loginUIM.uimApi.getAuthenticatedUser();
  } catch (e) {
    // Not logged in.
    return false;
  }
};

loginUIM.isLoggedIn = () => !!loginUIM.uimApi.authenticatedUser;

/**
 * Takes in the registration items, selects those that are checked, returns policies and newsletters in format UIM
 * expects when submitting back to them
 */
loginUIM.getSelectedRegistrationItems = (items) => {
  const resp = {
    policies: {},
    newsletters: [],
  };

  if (items) {
    // per app configuration, either implicit or explicit items may be empty array.
    if (items.implicitPolicies && items.explicitPolicies) {
      // turn implicit and explicit policy arrays into an object of policy id set to true when checked
      const policies = items.implicitPolicies.concat(items.explicitPolicies);
      policies.forEach((p) => {
        if (p.checked) {
          // the id could be one or an comma delimited string
          const selected = p.id.split(',');
          selected.forEach((s) => (resp.policies[s] = true));
        }
      });
    }

    if (items.newsletters && items.newsletters.length) {
      // turn newsletters array into an array of selected newsletters when checked
      const newsletters = items.newsletters.filter((n) => n.checked);
      newsletters.forEach((n) =>
        resp.newsletters.push({
          newsletterId: n.id,
        })
      );
    }
  }

  return resp;
};

/**
 * Given the UIM sdk return the info we want to pass along
 * @returns {Promise<void>}
 */
loginUIM.getUimClientInfo = async () => {
  // getJWT function will set authenticated user if none exists
  const id_token = await loginUIM.getJWT();

  // attempting to get the user will now succeed
  const user = loginUIM.getAuthenticatedUser();
  const access_token = user && (await user.getAccessToken());
  const refresh_token = user && (await user.getRefreshToken());

  return {
    uid: user && UIM_USER_ID_FIELD && user[UIM_USER_ID_FIELD],
    access_token,
    refresh_token,
    id_token,
  };
};

const getResetPasswordToken = function () {
  const regex = /.+uimResetPasswordToken=(.+)/g;
  const match = regex.exec(window.location.href);
  return match && match.length === 2 ? match[1] : null;
};

const initializeUim = async (
  appUserCallback,
  newPasswordRequiredCallback,
  loggedInCallback,
  sessionInvalidCallback,
  workflowErrorCallback,
  registrationRejectedCallback,
  accountVerifiedCallback,
  configData,
  autoLoginTokens,
  autoRefreshTimeout
) => {
  let uimApiPromiseBuilder = UimWebSdk.setupUimApi()
    .withEnvironment(UIM_SDK_ENV)
    .withApplicationId(getOAuthApplicationId() || UIM_APP_ID)
    .withAutoRefresh(autoRefreshTimeout)
    .withCountry(configData.country)
    .withLanguage(configData.language);

  // add campaign attribution if one exists
  if (configData.application) {
    uimApiPromiseBuilder = uimApiPromiseBuilder.withSource(
      configData.application
    );
  }

  // autoLoginTokens can contain tokens an external app wants to use to override the current session's tokens
  if (
    autoLoginTokens.access_token &&
    autoLoginTokens.id_token &&
    autoLoginTokens.refresh_token
  ) {
    // only call withTokens if we have the requisite parameters
    uimApiPromiseBuilder = uimApiPromiseBuilder.withTokens(
      autoLoginTokens.access_token,
      autoLoginTokens.id_token,
      autoLoginTokens.refresh_token
    );
  }

  uimApiPromiseBuilder
    // .withAdvertisingId('advertising_id_test', 'ios') // 'android'
    .onNewPasswordRequired(newPasswordRequiredCallback)
    .onNewMandatoryInformation(loggedInCallback)
    .onRegistrationRejected(registrationRejectedCallback)
    .onAccountVerified(accountVerifiedCallback)
    .onLoginWithNoMissingItems(loggedInCallback)
    .onSessionInvalid(sessionInvalidCallback)
    .onWorkflowError(workflowErrorCallback);

  loginUIM.uimApiPromise = uimApiPromiseBuilder.initialize();

  try {
    loginUIM.uimApi = await loginUIM.uimApiPromise;
    await fetchAuthenticatedUser();

    const passwordRegex = _get(
      loginUIM.uimApi,
      'applicationService.applicationConfig.passwordRegex',
      '^.{5,}$'
    );
    loginUIM.passwordRegExp = new RegExp(passwordRegex);
    loginUIM.resetPasswordToken = getResetPasswordToken();

    // notify app of user's state
    appUserCallback(loginUIM.isLoggedIn());
  } catch (e) {
    logException(e);
  }
};

loginUIM.uimWebSdk = UimWebSdk;
loginUIM.init = initializeUim;

export default loginUIM;
